import { Component, DestroyRef, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { distinctUntilChanged, skip, startWith } from 'rxjs/operators';

import { Loading } from 'src/shared/components/loading/loading';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Addresses } from 'src/providers/customer/addresses';
import { Logger } from 'src/providers/logger';
import { PhotoNotes, PhotoNoteType } from 'src/providers/photo-notes/photo-notes';
import { Integrations } from 'src/providers/integrations/integrations';
import { Util } from 'src/shared/util/util';
import { WindowService } from 'src/shared/providers/window.service';
import { AddressGuidebookService } from 'src/shared/services/address-guidebook.service';
import { buildGuidebookModel, entryUpsert, GuidebookModel } from 'src/shared/services/guidebook-entries';
import { isRentalAccount } from 'src/shared/services/guidebook-address-fields';
import { buildCustomFieldsPatch, buildGuestAddressPatch, buildProAddressPatch } from 'src/shared/services/guidebook-address-patch';
import {
  FREE_PARKING_SPOT_OPTIONS,
  MAX_PARKING_COST_OPTIONS,
  PAID_PARKING_SPOT_OPTIONS,
  PARKING_PAY_OPTIONS,
  PARKING_TYPE_OPTIONS,
} from 'src/shared/constants/parking-options';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { PhotoNoteUrlModel } from 'src/shared/components/photo-caption/photo-caption.component';
import { PhotoCaptionService } from 'src/shared/components/photo-caption/photo-caption.service';

import { AddressModel } from 'src/models/address.model';
import { TidySelectBooleanValueModel, TidySelectStringValueModel, TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';
import { ConfirmPage, ConfirmPageParamsModel } from '../confirm/confirm.component';
import { Client } from 'src/providers/client/client';

const CHECKOUT_SETTING_KEY = 'check_out_instructions';

type SectionKey = 'pro' | 'guest' | 'custom';

interface SectionStatus {
  state: 'complete' | 'partial' | 'empty';
  label: string;
}

@Component({
    templateUrl: 'edit-property.html',
    encapsulation: ViewEncapsulation.None,
    providers: [PhotoCaptionService],
    standalone: false
})
export class EditPropertyPage implements OnInit {
  private readonly addresses = inject(Addresses);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly integrations = inject(Integrations);
  private readonly logger = inject(Logger);
  private readonly navCtrl = inject(CustomNavController);
  private readonly photoCaptionService = inject(PhotoCaptionService);
  private readonly photoNotes = inject(PhotoNotes);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly route = inject(ActivatedRoute);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly modalController = inject(ModalController);
  private readonly client = inject(Client);
  private readonly windowService = inject(WindowService);
  private readonly addressGuidebookService = inject(AddressGuidebookService);

  accessPhotoUrls: PhotoNoteUrlModel[] = [];
  address: AddressModel;
  /** The rest of the address under the page header: whatever the header itself doesn't already show. */
  addressLine = '';
  boundSave = this.save.bind(this);
  closingPhotoUrls: PhotoNoteUrlModel[] = [];
  customFieldDefinitions: any[] = [];
  customFieldValues: { [key: string]: any } = {};
  private loadedCustomFieldValues: { [key: string]: any } = {};
  dialogParams: any;
  errorMessage: string;
  // Every section starts closed; its status chip shows what's left to fill in.
  expandedSections: Record<SectionKey, boolean> = { pro: false, guest: false, custom: false };
  readonly form = this.fb.group({
    propertyName: this.fb.nonNullable.control(''),
    paid_parking: this.fb.control<boolean | null>(null),
    parking_spot: this.fb.control<string | null>(null),
    parking_pay_with: this.fb.control<string | null>(null),
    max_parking_cost: this.fb.control<number | null>(null),
    parking_notes: this.fb.nonNullable.control(''),
    home_access: this.fb.nonNullable.control(''),
    home_close: this.fb.nonNullable.control(''),
    guest_paid_parking: this.fb.control<boolean | null>(null),
    guest_parking_spot: this.fb.control<string | null>(null),
    guest_parking_pay_with: this.fb.control<string | null>(null),
    guest_max_parking_cost: this.fb.control<number | null>(null),
    guest_parking_notes: this.fb.nonNullable.control(''),
    guest_home_access: this.fb.nonNullable.control(''),
    check_out_instructions: this.fb.nonNullable.control(''),
  });
  freeParkingSpots: TidySelectStringValueModel[];
  guestAccessPhotoUrls: PhotoNoteUrlModel[] = [];
  guestParkingPhotoUrls: PhotoNoteUrlModel[] = [];
  /** Rental accounts keep a second set of instructions for guests, shown in the guidebook. */
  readonly isRentalClient = isRentalAccount();
  isRightSideContent = true;
  loaded: boolean;
  maxCostOptions: TidySelectNumberValueModel[];
  paidParkingSpots: TidySelectStringValueModel[];
  parkingPhotoUrls: PhotoNoteUrlModel[] = [];
  parkingTypes: TidySelectBooleanValueModel[];
  payOptions: TidySelectStringValueModel[];
  propertyId: number;
  sectionStatus: { pro: SectionStatus | null; guest: SectionStatus | null } = { pro: null, guest: null };
  /** False when the guidebook settings couldn't be loaded, so the checklist can't be edited safely. */
  showCheckoutChecklist = false;
  submitted: boolean;
  private guidebookModel: GuidebookModel | null = null;
  private parkingWatchersStarted = false;
  private sectionsStatusWatcherStarted = false;

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.rightSidePanelService.setDialogPageTitle('Edit Property');
    this.setFormOptions();
    this.loadData();
  }

  @Loading('', true)
  async loadData() {
    try {
      this.rightSidePanelService.setDialogLoading(true);
      this.loaded = false;
      this.propertyId = this.route.snapshot.params['addressId'] || await this.getParam('addressId') || await this.getParam('propertyId');
      // The guest checkout checklist is a guidebook setting, not an address column. Its fetch
      // failing shouldn't take the rest of the page down; the checklist card just hides.
      const [address, addressWithSettings] = await Promise.all([
        this.addresses.getAddress(this.propertyId),
        this.isRentalClient ? this.loadGuidebookSettings(this.propertyId) : Promise.resolve(null),
      ]);
      this.address = address;
      this.addressLine = this.buildAddressLine(address);
      this.guidebookModel = addressWithSettings ? buildGuidebookModel(addressWithSettings, true) : null;
      this.showCheckoutChecklist = this.isRentalClient && !!this.checkoutEntry();
      this.parkingPhotoUrls = this.photoCaptionService.mountPhotoUrls(this.address?.photo_notes || [], 'parking');
      this.accessPhotoUrls = this.photoCaptionService.mountPhotoUrls(this.address?.photo_notes || [], 'access');
      this.closingPhotoUrls = this.photoCaptionService.mountPhotoUrls(this.address?.photo_notes || [], 'closing');
      this.guestParkingPhotoUrls = this.photoCaptionService.mountPhotoUrls(this.address?.photo_notes || [], 'guest_parking');
      this.guestAccessPhotoUrls = this.photoCaptionService.mountPhotoUrls(this.address?.photo_notes || [], 'guest_access');
      this.form.patchValue({
        propertyName: this.address?.address_name || '',
        paid_parking: this.address?.paid_parking ?? null,
        parking_spot: this.address?.parking_spot ?? null,
        parking_pay_with: this.address?.parking_pay_with ?? null,
        max_parking_cost: this.address?.max_parking_cost ?? null,
        parking_notes: this.address?.parking_notes ?? '',
        home_access: this.address?.home_access ?? '',
        home_close: this.address?.home_close ?? '',
        guest_paid_parking: this.address?.guest_paid_parking ?? null,
        guest_parking_spot: this.address?.guest_parking_spot ?? null,
        guest_parking_pay_with: this.address?.guest_parking_pay_with ?? null,
        guest_max_parking_cost: this.address?.guest_max_parking_cost ?? null,
        guest_parking_notes: this.address?.guest_parking_notes ?? '',
        guest_home_access: this.address?.guest_home_access ?? '',
        check_out_instructions: this.checkoutEntry()?.value ?? '',
      });
      this.watchParkingTypes();
      this.watchSectionStatus();

      // Load custom field definitions for Address model type
      this.customFieldDefinitions = await this.integrations.getCustomFields('Address');

      // Initialize values from address.custom_fields (which is an object keyed by field_key)
      const addressCustomFields = this.address?.custom_fields || {};
      this.loadedCustomFieldValues = {};
      if (addressCustomFields && typeof addressCustomFields === 'object' && !Array.isArray(addressCustomFields)) {
        this.loadedCustomFieldValues = { ...addressCustomFields };
      }
      this.customFieldValues = { ...this.loadedCustomFieldValues };
      this.setValidators();
      this.errorMessage = '';
      this.loaded = true;
      this.rightSidePanelService.setDialogLoading(false);
    } catch (err) {
      this.rightSidePanelService.setDialogLoading(false);
      this.errorMessage = err.message || 'Failed to load property data';
    }
  }

  async getParam(paramName: string): Promise<any> {
    let param = this.dialogParams?.[paramName];
    if (param === null || param === undefined) {
      param = this.navCtrl.getParam(paramName);
    }
    if (param === null || param === undefined) {
      param = await this.storage.retrieve(paramName);
    }
    return param;
  }

  setFormOptions() {
    this.parkingTypes = PARKING_TYPE_OPTIONS;
    this.paidParkingSpots = PAID_PARKING_SPOT_OPTIONS;
    this.freeParkingSpots = FREE_PARKING_SPOT_OPTIONS;
    this.payOptions = PARKING_PAY_OPTIONS;
    this.maxCostOptions = MAX_PARKING_COST_OPTIONS;
  }

  setValidators() {
    // No required validators - all fields are optional
  }

  clearFields() {
    this.form.patchValue({
      parking_spot: null,
      parking_pay_with: null,
      max_parking_cost: null
    });
    this.setValidators();
  }

  clearGuestFields() {
    this.form.patchValue({
      guest_parking_spot: null,
      guest_parking_pay_with: null,
      guest_max_parking_cost: null
    });
  }

  // tidy-select reports a pick even when it's the same option, which shouldn't wipe the
  // spot and payment details, so only a real change of parking type clears them.
  private watchParkingTypes() {
    if (this.parkingWatchersStarted) { return; }
    this.parkingWatchersStarted = true;
    this.onParkingTypeChange(this.form.controls.paid_parking, () => this.clearFields());
    this.onParkingTypeChange(this.form.controls.guest_paid_parking, () => this.clearGuestFields());
  }

  private onParkingTypeChange(control: FormControl<boolean | null>, clear: () => void) {
    control.valueChanges.pipe(
      startWith(control.value),
      distinctUntilChanged(),
      skip(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => clear());
  }

  toggleSection(key: SectionKey) {
    this.expandedSections = { ...this.expandedSections, [key]: !this.expandedSections[key] };
  }

  private buildAddressLine(address: AddressModel): string {
    if (!address) { return ''; }
    const cityZip = address.city && address.zip ? `${address.city}, ${address.zip}` : '';
    const parts = address.address_name ? [address.address1, address.address2, cityZip] : [address.address2, cityZip];
    return parts.filter(Boolean).join(' · ');
  }

  private watchSectionStatus() {
    if (this.sectionsStatusWatcherStarted) { return; }
    this.sectionsStatusWatcherStarted = true;
    this.form.valueChanges.pipe(
      startWith(this.form.value),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.refreshSectionStatus());
  }

  // Counts the written instructions only: photos back them up, but on their own they don't explain the property.
  private refreshSectionStatus() {
    const value = this.form.getRawValue();
    const hasText = (text: string | null) => !!text?.trim();
    this.sectionStatus = {
      pro: this.toSectionStatus([
        value.paid_parking !== null || !!value.parking_spot || hasText(value.parking_notes),
        hasText(value.home_access),
        hasText(value.home_close),
      ]),
      guest: this.isRentalClient ? this.toSectionStatus([
        value.guest_paid_parking !== null || !!value.guest_parking_spot || hasText(value.guest_parking_notes),
        hasText(value.guest_home_access),
        ...(this.showCheckoutChecklist ? [hasText(value.check_out_instructions)] : []),
      ]) : null,
    };
  }

  private toSectionStatus(filled: boolean[]): SectionStatus {
    const count = filled.filter(Boolean).length;
    if (count === filled.length) { return { state: 'complete', label: 'Complete' }; }
    if (count === 0) { return { state: 'empty', label: 'Not started' }; }
    return { state: 'partial', label: 'In Progress' };
  }

  private async loadGuidebookSettings(addressId: number) {
    try {
      return await this.addressGuidebookService.getAddressWithSettings(addressId);
    } catch (err) {
      this.logger.error(err, 'load guidebook settings error');
      return null;
    }
  }

  @Loading('', true)
  async save() {
    this.submitted = true;
    if (!this.form.valid) {
      return;
    }
    try {
      await this.uploadPhotos();
      // Both sets send every change, cleared ones as null, so clearing a field reaches the pro or guest.
      const payload: any = buildProAddressPatch(this.address, this.form.getRawValue());
      if (this.isRentalClient) {
        Object.assign(payload, buildGuestAddressPatch(this.address, this.form.getRawValue()));
      }
      const propertyName = this.form.value.propertyName?.trim();
      if (propertyName) {
        payload.address_name = propertyName;
      }

      const customFieldsPayload = buildCustomFieldsPatch(
        this.customFieldDefinitions.map(definition => definition.field_key),
        this.loadedCustomFieldValues,
        this.customFieldValues,
      );
      if (Object.keys(customFieldsPayload).length > 0) {
        payload.custom_fields = customFieldsPayload;
      }

      await this.addresses.update(payload, this.address.id);
      await this.saveCheckoutChecklist();
      this.util.showSuccess('Property Saved');
      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.back();
      }
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  private checkoutEntry() {
    return this.guidebookModel?.entries.find(entry => entry.key === CHECKOUT_SETTING_KEY) ?? null;
  }

  // Written exactly as the guidebook editor writes it, so both places stay in sync.
  private async saveCheckoutChecklist() {
    const entry = this.checkoutEntry();
    if (!this.showCheckoutChecklist || !entry) { return; }
    const value = this.form.value.check_out_instructions || '';
    if (value === entry.value) { return; }
    const row = entryUpsert({ ...entry, value }, this.guidebookModel.categoryVisibility);
    await this.addressGuidebookService.upsertGuidebook(this.address.id, [row]);
  }

  async uploadPhotos() {
    try {
      const photoSets: [PhotoNoteUrlModel[], PhotoNoteType][] = [
        [this.accessPhotoUrls, 'access'],
        [this.parkingPhotoUrls, 'parking'],
        [this.closingPhotoUrls, 'closing'],
      ];
      if (this.isRentalClient) {
        photoSets.push([this.guestParkingPhotoUrls, 'guest_parking'], [this.guestAccessPhotoUrls, 'guest_access']);
      }
      for (const [photoUrls, type] of photoSets) {
        await this.photoCaptionService.uploadPhotosGeneric(
          photoUrls,
          type,
          (s3PhotoUrlKey: string) => this.photoNotes.saveAddressPhotoNote(type, s3PhotoUrlKey, this.address.id),
          (photoNoteId: number, caption: string) => this.photoNotes.photoCaption(photoNoteId, caption),
          'room'
        );
      }
    } catch (err) {
      this.logger.error(err, 'upload image error');
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  async getImg(name: string) {
    try {
      const newPhotoNote = await this.photoCaptionService.getImg(name);
      switch (name) {
        case 'parking':
          this.parkingPhotoUrls.push(newPhotoNote);
          break;
        case 'access':
          this.accessPhotoUrls.push(newPhotoNote);
          break;
        case 'closing':
          this.closingPhotoUrls.push(newPhotoNote);
          break;
        case 'guest_parking':
          this.guestParkingPhotoUrls.push(newPhotoNote);
          break;
        case 'guest_access':
          this.guestAccessPhotoUrls.push(newPhotoNote);
          break;
        default: null
      }
    } catch (err) {
      this.logger.error(err, 'upload address image error');
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  async confirmDelete(photoId: number) {
    try {
      await this.photoCaptionService.confirmDelete(
        photoId,
        [...this.accessPhotoUrls, ...this.parkingPhotoUrls, ...this.closingPhotoUrls, ...this.guestParkingPhotoUrls, ...this.guestAccessPhotoUrls]
      );
    } catch (err) {
      this.logger.error(err, 'delete image error');
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  onCustomFieldValueChange(fieldKey: string, value: any): void {
    this.customFieldValues[fieldKey] = value;
  }


  async deleteAddress() {
    if (this.address.plans?.length) {
      const message = 'This property has scheduled jobs. Please cancel the jobs for this property before deleting this property.';
      return this.util.showError(message, 10000);
    }
    const confirmParams: ConfirmPageParamsModel = {
      title: 'Delete Property?',
      body: 'Are you sure you want to delete this property?',
      backText: 'Go Back',
      confirmText: 'Delete Property',
      confirmAction: this.confirmDeleteAddress.bind(this)
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: confirmParams,
      animated: false,
      cssClass: 'confirm-modal'
    });
    confirmationModal.present();
  }

  async confirmDeleteAddress() {
    try {
      await this.addresses.deleteAddress(this.address.id);
      const addresses = await this.client.getAddresses(true);
      await this.storage.save('addresses', addresses);
      localStorage.removeItem('addressId');
      await this.storage.save('didJustDeleteProperty', true);
      this.navCtrl.navigateForward('properties');
      this.modalController.dismiss();
    } catch(err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }
}
